import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { MdClose, MdInfo } from 'react-icons/md';
import { ITransaction } from '../../../interfaces/Transaction';
import { useAppSelector } from '../../../hooks';
import Chip from '../../../components/Chip';
import { HoverDropdown } from '../../../components/HoverDropDown';
import transactionsApi from '../../../api/transactions';
import useCurrency from '../../../hooks/useCurrency';

export default function Transactions() {
  const { userData } = useAppSelector((state) => state.userSlice);
  const [transactions, setTransactions] = useState<ITransaction[]>([]);

  useEffect(() => {
    if (userData) {
      transactionsApi.get(userData._id).then((data) => {
        setTransactions(data);
      });
    }
  }, []);

  return (
    <div className="w-full h-full flex flex-col gap-5 py-4">
      <h1 className="text-lg font-bold mb-2">Transactions and Receipts</h1>
      {transactions.length === 0 ? (
        <div className="w-full flex items-center justify-center min-h-[300px]">
          <h1 className="text-2xl font-semibold">No Transactions.</h1>
        </div>
      ) : (
        <div className="w-full flex flex-col grow custom-scrollbar overflow-x-scroll">
          <div className="w-full h-full custom-scrollbar">
            <table className="min-w-full table-auto">
              <thead className="bg-[#F4F7FCBF] py-4 px-2 text-left">
                <tr>
                  <th className="text-xs font-bold py-4 px-2 text-left"></th>
                  <th className="text-xs font-bold py-4 px-2 text-left">DATE </th>
                  <th className="text-xs font-bold py-4 px-2 text-left">TRANSACTION ID </th>
                  <th className="text-xs font-bold py-4 px-2 text-left">NAME </th>
                  <th className="text-xs font-bold py-4 px-2 text-left">PLAN </th>
                  <th className="text-xs font-bold py-4 px-2 text-left">AMT </th>
                  <th className="text-xs font-bold py-4 px-2 text-left">STATUS </th>
                  <th className="text-xs font-bold py-4 px-2 text-left"></th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => (
                  <TableRow
                    key={transaction._id}
                    transaction={transaction}
                    index={index}
                    timezone={(userData && userData.preference && userData.preference.timezone) || 'IST'}
                    currency={
                      (userData &&
                        userData.preference &&
                        userData.preference.currency &&
                        userData.preference.currency.code) ||
                      'INR'
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function TableRow({
  index,
  transaction,
  timezone,
}: {
  index: number;
  transaction: ITransaction;
  timezone: string;
  currency: string;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const convertAmount = useCurrency();

  return (
    <>
      <tr className="even:bg-[#F4F7FCBF]">
        <td className="text-sm font-semibold py-4 px-2 text-left align-top">{index + 1}</td>
        <td className="text-sm font-semibold py-4 px-2 text-left align-top ">
          {formatInTimeZone(transaction.createdAt, timezone || 'IST', 'd LLL y')}
        </td>
        <td className="text-sm font-semibold py-4 px-2 text-left align-top">{transaction._id}</td>
        <td className="text-sm font-semibold py-4 px-2 text-left align-top">
          <div className="flex flex-col">
            <p>{transaction.plan.user.name}</p>
            <p className="text-[10px] text-gray-400">{transaction.plan.user.phoneNumber}</p>
          </div>
        </td>
        <td className="text-sm font-semibold py-4 px-2 text-left align-top">
          <div className="flex flex-col">
            <p>{transaction.plan.purchasedSessions}</p>
            <p className="text-[10px] text-gray-400">Sessions</p>
          </div>{' '}
        </td>
        <td className="text-sm font-semibold py-4 px-2 text-left align-top text-green-400">
          {/* <span className="uppercase pr-1">{currency}</span> */}
          {convertAmount(transaction.amount).amountString}
        </td>
        <td className="text-sm font-semibold py-4 px-2 text-left align-top">
          <Chip
            state={
              transaction.status === 'succeeded'
                ? 'success'
                : transaction.status === 'failed'
                  ? 'error'
                  : transaction.status === 'processing'
                    ? 'warning'
                    : 'pending'
            }
          >
            <p>{transaction.status}</p>
          </Chip>
        </td>
        <td className="text-sm font-semibold py-4 px-2 text-left align-top">
          <HoverDropdown
            menu={[
              {
                key: 1,
                label: (
                  <div
                    onClick={() => setShowDetails(true)}
                    className="flex items-center p-2 px-4 hover:bg-gray-100 cursor-pointer"
                  >
                    <p className="text-sm font-medium pr-2">View</p>
                    <MdInfo className="text-blue-400" />
                  </div>
                )
              }
            ]}
            placement="bottomLeft"
          >
            <BsThreeDotsVertical className="text-sm cursor-pointer" />
          </HoverDropdown>
        </td>
      </tr>
      {showDetails && (
        <div className="fixed top-0 left-0 w-full h-full z-50">
          <div className="relative w-full h-screen flex justify-center -z-10">
            <div
              onClick={() => setShowDetails(false)}
              className="absolute top-0 left-0 w-full h-full bg-black/70 -z-10"
            ></div>
            <div id="portal" className="w-[90%] h-[80%] mx-auto my-auto flex justify-center">
              <div className="max-w-[768px] w-full h-max bg-white flex flex-col gap-4 px-10 py-7 rounded-xl">
                <div className="relative  w-full flex items-center justify-center">
                  <h1 className="text-lg font-bold text-center">Transaction Receipt</h1>
                  <MdClose
                    className="absolute right-0 text-base cursor-pointer"
                    onClick={() => setShowDetails(false)}
                  />
                </div>
                <div className="w-full flex flex-col items-center gap-4">
                  <p className="text-sm font-semibold">13 NOV 2022, 5:40 pm</p>
                  <h3 className="text-base font-bold">Thank you for purchasing a plan with us ! </h3>
                </div>

                <div className="w-full flex flex-col gap-2 mt-10">
                  <div className="w-full flex items-center">
                    <p className="w-2/5 text-sm font-medium">Transaction ID</p>
                    <p className="text-xs font-semibold">{transaction._id}</p>
                  </div>
                  <div className="w-full flex items-center">
                    <p className="w-2/5 text-sm font-medium">Customer name</p>
                    <p className="text-sm font-semibold">{transaction.plan.user.name}</p>
                  </div>
                  <div className="w-full flex items-center">
                    <p className="w-2/5 text-sm font-medium">Email Id</p>
                    <p className="text-sm font-semibold">{transaction.plan.user.email}</p>
                  </div>
                </div>

                <div className="w-full flex flex-col gap-2 mt-10">
                  <table className="table-auto">
                    <thead className="bg-[#F4F7FCBF] py-4 px-2 text-left mb-1">
                      <tr>
                        <th className="text-xs font-bold py-4 px-2 text-left">PLAN </th>
                        {transaction.plan.tutor && (
                          <th className="text-xs font-bold py-4 px-2 text-left">TRAINER NAME</th>
                        )}
                        <th className="text-xs font-bold py-4 px-2 text-left">DISCOUNT</th>
                        <th className="text-xs font-bold py-4 px-2 text-left">PRICE</th>
                      </tr>
                    </thead>
                    <tbody className="">
                      <tr className="bg-[#F4F7FCBF]">
                        <td className="text-sm font-semibold py-4 px-2 text-left align-top mt-2">
                          <div className="flex flex-col">
                            <p>{transaction.plan.purchasedSessions}</p>
                            <p className="text-[10px] text-gray-400">Sessions</p>
                          </div>
                        </td>
                        {transaction.plan.tutor && (
                          <td className="text-sm font-semibold py-4 px-2 text-left align-top mt-2">
                            {transaction.plan.tutor.personal.name}
                          </td>
                        )}
                        <td className="text-sm font-semibold py-4 px-2 text-left align-top mt-2">
                          {transaction.plan.discount}%
                        </td>
                        <td className="text-sm font-semibold text-green-400 py-4 px-2 text-left align-top mt-2">
                          {transaction.plan.totalCost / transaction.plan.purchasedSessions}x
                          {transaction.plan.purchasedSessions}=
                          <span className="uppercase pr-1">{transaction.currency}</span>
                          {transaction.plan.totalCost}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
